/* Enhance personalization and catalog sorting while retaining native form values/events. */
(() => {
  const entries = new Map();
  let serial = 0;
  let opened;
  let frame;
  const selector = '#teeFormInputs select:not([multiple]), #MainContent facet-filters-form select[name="sort_by"]';

  function close(entry = opened) {
    if (!entry) return;
    entry.list.hidden = true;
    entry.button.setAttribute('aria-expanded', 'false');
    entry.button.removeAttribute('aria-activedescendant');
    if (opened === entry) opened = null;
  }
  function position(entry) {
    const rect = entry.button.getBoundingClientRect();
    const below = innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const upward = below < 180 && above > below;
    const height = Math.max(60, Math.min(300, upward ? above : below));
    let width = rect.width;
    if (entry.list.classList.contains('gp-catalog-select-list')) {
      entry.list.style.width = 'max-content';
      width = Math.max(width, entry.list.getBoundingClientRect().width);
    }
    width = Math.min(width, innerWidth - 16);
    Object.assign(entry.list.style, {
      left: `${Math.max(8, Math.min(rect.left, innerWidth - width - 8))}px`,
      width: `${width}px`,
      maxHeight: `${height}px`,
      top: upward ? 'auto' : `${rect.bottom + 5}px`,
      bottom: upward ? `${innerHeight - rect.top + 5}px` : 'auto',
    });
  }
  function activate(entry, index) {
    entry.active = index;
    for (const item of entry.list.children) item.classList.toggle('is-active', Number(item.dataset.index) === index);
    const item = entry.list.querySelector(`[data-index="${index}"]`);
    if (item) {
      entry.button.setAttribute('aria-activedescendant', item.id);
      const top = item.offsetTop;
      if (top < entry.list.scrollTop) entry.list.scrollTop = top;
      else if (top + item.offsetHeight > entry.list.scrollTop + entry.list.clientHeight) entry.list.scrollTop = top + item.offsetHeight - entry.list.clientHeight;
    }
  }
  function isPlaceholder(option) {
    return option.disabled && (option.value === '' || option.value === 'null' || option.textContent.trim().toLowerCase() === 'choose an option');
  }
  function enabled(entry) {
    return Array.from(entry.select.options).map((option, index) => ({ option, index }))
      .filter(({ option }) => !option.hidden && !option.disabled && !option.closest('optgroup')?.disabled)
      .map(({ index }) => index);
  }
  function open(entry) {
    if (entry.select.disabled) return;
    close();
    sync(entry);
    opened = entry;
    entry.list.hidden = false;
    entry.button.setAttribute('aria-expanded', 'true');
    position(entry);
    const choices = enabled(entry);
    activate(entry, choices.includes(entry.select.selectedIndex) ? entry.select.selectedIndex : (choices[0] ?? -1));
  }
  function choose(entry, index) {
    if (!enabled(entry).includes(index)) return;
    const changed = entry.select.selectedIndex !== index;
    entry.select.selectedIndex = index;
    close(entry);
    entry.button.focus({ preventScroll: true });
    if (changed) {
      entry.select.dispatchEvent(new Event('input', { bubbles: true }));
      entry.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    sync(entry);
  }
  function sync(entry) {
    const { select, button, list } = entry;
    const text = select.selectedOptions[0]?.textContent.trim() || 'Choose an option';
    if (entry.label.textContent !== text) entry.label.textContent = text;
    button.disabled = select.disabled;
    const heading = select.closest('.tee-field')?.querySelector('.tee-field__heading')?.textContent.trim();
    const name = select.getAttribute('aria-label') || heading || select.labels?.[0]?.textContent.trim() || 'Choose an option';
    button.setAttribute('aria-label', name);
    button.setAttribute('aria-required', String(select.required || !!select.closest('.tee-field')?.querySelector('.tee-asterisk')));
    button.setAttribute('aria-invalid', select.getAttribute('aria-invalid') || 'false');
    const signature = JSON.stringify(Array.from(select.options).map(option => [option.textContent, option.disabled, option.hidden, option.selected, option.closest('optgroup')?.disabled]));
    if (signature !== entry.signature) {
      entry.signature = signature;
      list.replaceChildren();
      Array.from(select.options).forEach((option, index) => {
        if (option.hidden || isPlaceholder(option)) return;
        const item = document.createElement('div');
        item.className = 'gp-select-option';
        item.id = `${list.id}-${index}`;
        item.dataset.index = index;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(option.selected));
        item.setAttribute('aria-disabled', String(option.disabled || !!option.closest('optgroup')?.disabled));
        item.textContent = option.textContent.trim();
        list.append(item);
      });
      if (opened === entry) activate(entry, entry.active);
    }
    if (select.disabled) close(entry);
  }
  function attach(select) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gp-select-trigger';
    const catalog = !!select.closest('facet-filters-form');
    if (catalog) button.classList.add('gp-catalog-select-trigger');
    if (select.hasAttribute('aria-describedby')) button.setAttribute('aria-describedby', select.getAttribute('aria-describedby'));
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    const label = document.createElement('span');
    label.className = 'gp-select-value';
    button.append(label);
    const list = document.createElement('div');
    list.className = 'gp-select-list';
    if (catalog) list.classList.add('gp-catalog-select-list');
    list.id = `gp-select-list-${++serial}`;
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    button.setAttribute('aria-controls', list.id);
    const entry = { select, button, label, list, active: -1, signature: '', oldTab: select.getAttribute('tabindex'), oldHidden: select.getAttribute('aria-hidden'), query: '', lastKey: 0 };
    entries.set(select, entry);
    select.after(button);
    document.body.append(list);
    select.classList.add('gp-select-native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    button.addEventListener('click', () => opened === entry ? close(entry) : open(entry));
    list.addEventListener('pointerdown', event => event.preventDefault());
    list.addEventListener('click', event => {
      const item = event.target.closest('[data-index]');
      if (item) choose(entry, Number(item.dataset.index));
    });
    button.addEventListener('keydown', event => {
      const choices = enabled(entry);
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (opened !== entry) { open(entry); if (event.key.startsWith('Arrow')) return; }
        let pos = choices.indexOf(entry.active);
        if (event.key === 'Home') pos = 0;
        else if (event.key === 'End') pos = choices.length - 1;
        else pos = Math.max(0, Math.min(choices.length - 1, pos + (event.key === 'ArrowDown' ? 1 : -1)));
        activate(entry, choices[pos] ?? -1);
      } else if (event.key === 'Escape') {
        if (opened === entry) {
          event.preventDefault();
          if (catalog) { event.stopPropagation(); entry.handledEscape = true; }
          close(entry);
        }
      } else if (event.key === 'Tab') close(entry);
      else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (opened === entry) choose(entry, entry.active); else open(entry);
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        const now = Date.now();
        entry.query = (now - entry.lastKey < 700 ? entry.query : '') + event.key.toLowerCase();
        entry.lastKey = now;
        if (opened !== entry) open(entry);
        const match = choices.find(index => select.options[index].textContent.trim().toLowerCase().startsWith(entry.query));
        if (match !== undefined) activate(entry, match);
      }
    });
    button.addEventListener('keyup', event => {
      if (event.key === 'Escape' && entry.handledEscape) {
        event.stopPropagation();
        entry.handledEscape = false;
      }
    });
    entry.onChange = () => sync(entry);
    entry.onFocus = () => button.focus();
    entry.onInvalid = event => { event.preventDefault(); button.setAttribute('aria-invalid', 'true'); button.focus(); open(entry); button.setAttribute('aria-invalid', 'true'); };
    select.addEventListener('change', entry.onChange);
    select.addEventListener('input', entry.onChange);
    select.addEventListener('focus', entry.onFocus);
    select.addEventListener('invalid', entry.onInvalid);
    sync(entry);
  }
  function refresh() {
    frame = null;
    for (const [select, entry] of entries) {
      if (!select.isConnected || !entry.button.isConnected) {
        close(entry);
        entry.button.remove(); entry.list.remove();
        select.classList.remove('gp-select-native');
        for (const [name, value] of [['tabindex', entry.oldTab], ['aria-hidden', entry.oldHidden]]) {
          if (value === null) select.removeAttribute(name); else select.setAttribute(name, value);
        }
        select.removeEventListener('change', entry.onChange);
        select.removeEventListener('input', entry.onChange);
        select.removeEventListener('focus', entry.onFocus);
        select.removeEventListener('invalid', entry.onInvalid);
        entries.delete(select);
      } else sync(entry);
    }
    document.querySelectorAll(selector).forEach(select => {
      if (!entries.has(select) && select.size <= 1) attach(select);
    });
  }
  const observer = new MutationObserver(records => {
    if (records.every(record => record.target.closest?.('.gp-select-trigger, .gp-select-list'))) return;
    if (!frame) frame = requestAnimationFrame(refresh);
  });
  observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'selected', 'label', 'value', 'aria-invalid'] });
  document.addEventListener('pointerdown', event => {
    if (opened && !opened.button.contains(event.target) && !opened.list.contains(event.target)) close();
  });
  document.addEventListener('focusin', event => { if (opened && event.target !== opened.button) close(); });
  window.addEventListener('resize', () => { if (opened) position(opened); });
  window.addEventListener('scroll', () => { if (opened) position(opened); }, true);
  refresh();
})();
